import {
  AttachmentOutlined,
  KeyboardArrowDown,
  KeyboardArrowUp,
} from "@mui/icons-material";
import isEmpty from "lodash/isEmpty";
import size from "lodash/size";
import { useMemo, useState } from "react";
import { useFormState } from "react-final-form";
import RffFileUploadField from "../../components/finalForm/inputs/RffFileUploadField";
import RffGroup from "../../components/finalForm/shared/RffGroup";
import Flx from "../../components/layout/Flx";
import ToggleTabSwitcher from "../../components/navigation/ToggleTabSwitcher";
import Txt from "../../components/typography/Txt";
import TitledCard from "../../components/ui/TitledCard/TitledCard";
import { useFileDocGroupAndTypesHook } from "../../hooks/useFileDocGroupAndTypesHook";
import { Divider } from "@mui/material";
import ToggleIconButton from "../../_src_shared/buttons/ToggleIconButton";
import SearchInput from "../../components/inputs/SearchInput";

const normalizeSearch = (str) => str?.toLowerCase()?.trim() || "";

const FileUploadSection = ({ defaultExpanded }) => {
  const { values } = useFormState();
  const [groupIndex, setGroupIndex] = useState(1);
  const [search, setSearch] = useState(null);
  const { fileDocGroups, getDocTypesByGroup } = useFileDocGroupAndTypesHook();

  const isSearching = Boolean(search?.trim());

  const activeDocGroup = useMemo(
    () => fileDocGroups[groupIndex - 1],
    [groupIndex, fileDocGroups],
  );

  // Filter doc groups based on search — a group matches if its name matches
  // OR if any of its doc types match the search term
  const filteredDocGroups = useMemo(() => {
    if (!isSearching) return fileDocGroups;
    const term = normalizeSearch(search);
    return fileDocGroups?.filter((gp) => {
      if (normalizeSearch(gp).includes(term)) return true;
      const types = getDocTypesByGroup(gp);
      return types?.some((dt) => normalizeSearch(dt).includes(term));
    });
  }, [fileDocGroups, search, isSearching, getDocTypesByGroup]);

  const docGroups = useMemo(() => {
    const groups = isSearching ? filteredDocGroups : fileDocGroups;
    if (isEmpty(values?.uploadMetadata)) {
      return groups;
    }
    return groups?.map((gp) => {
      const count = size(
        values?.uploadMetadata?.filter((f) => f?.docGroup === gp),
      );
      if (count === 0) return gp;
      return `${gp} (${count})`;
    });
  }, [values?.uploadMetadata, fileDocGroups, filteredDocGroups, isSearching]);

  // When searching, force "All" view to show all matching groups
  const effectiveGroupIndex = isSearching ? 0 : groupIndex;

  return (
    <TitledCard
      variant="h1"
      collapsible
      defaultExpanded={defaultExpanded}
      headerEndContent={
        <SearchInput value={search} onChange={setSearch} sx={{ mr: 1 }} />
      }
      icon={<AttachmentOutlined className="thin" />}
      title="Related Loan Files"
      cardSx={{
        ".titled-group-body-root": {
          p: 2,
        },
        "& .MuiToggleButtonGroup-root": {
          flexWrap: "wrap",
          ".MuiButtonBase-root": {
            fontSize: "11px",
          },
        },
      }}
    >
      {!isSearching && (
        <ToggleTabSwitcher
          tabs={["All", ...docGroups]}
          value={groupIndex}
          onChange={setGroupIndex}
          useIndex
        />
      )}
      <Flx gap={2} column sx={{ p: 1 }}>
        {effectiveGroupIndex === 0 ? (
          <Flx column fw g={2}>
            {(isSearching ? filteredDocGroups : fileDocGroups)?.length === 0 ? (
              <Txt
                body2
                sx={{ py: 2, textAlign: "center", color: "text.secondary" }}
              >
                No document groups or types match your search.
              </Txt>
            ) : (
              (isSearching ? filteredDocGroups : fileDocGroups)?.map(
                (gp, indx) => (
                  <>
                    {indx === 0 ? null : <Divider sx={{ mt: 1.4 }} />}
                    <RffDocGroupTypes
                      allowCollapse
                      key={gp}
                      docGroup={gp}
                      search={isSearching ? search : null}
                    />
                  </>
                ),
              )
            )}
          </Flx>
        ) : (
          <RffDocGroupTypes docGroup={activeDocGroup} />
        )}
      </Flx>
    </TitledCard>
  );
};

const RffDocGroupTypes = ({
  docGroup,
  allowCollapse = false,
  search = null,
}) => {
  const { getDocTypesByGroup } = useFileDocGroupAndTypesHook();
  const isSearching = Boolean(search?.trim());

  const docTypes = useMemo(() => {
    const allTypes = getDocTypesByGroup(docGroup);
    if (!isSearching) return allTypes;
    const term = normalizeSearch(search);
    // If the group name itself matches, show all its types
    if (normalizeSearch(docGroup).includes(term)) return allTypes;
    // Otherwise filter to only matching types
    return allTypes?.filter((dt) => normalizeSearch(dt).includes(term));
  }, [docGroup, search, isSearching, getDocTypesByGroup]);

  const [show, setShow] = useState(true);

  if (isEmpty(docTypes)) {
    return (
      <Flx column fw g={2}>
        <Txt body2>
          No document types available for this group. Please select another
          group.
        </Txt>
      </Flx>
    );
  }
  return (
    <Flx
      column
      fw
      g={2}
      sx={{
        ".MuiGrid-root .MuiTypography-root": {
          fontSize: "11px",
        },
      }}
    >
      <Flx fw jb ac>
        <Flx column sx={{ mt: 1 }}>
          <Txt bold>{docGroup}</Txt>
          <Txt body2>
            Drag & drop or click the following boxes to upload files.
          </Txt>
        </Flx>
        {allowCollapse && (
          <ToggleIconButton onClick={setShow}>
            {show ? <KeyboardArrowDown /> : <KeyboardArrowUp />}
          </ToggleIconButton>
        )}
      </Flx>
      {show && (
        <RffGroup columnSpacing={1} suppressBottomMargin rowSpacing={1}>
          {docTypes?.map((docType) => {
            return (
              <RffFileUploadField
                key={docType}
                name={`${docGroup}.${docType}`}
                uploadText={docType}
                size={3}
                height="72px"
                docType={docType}
                docGroup={docGroup}
                suppressButton
                borderSize={2}
                suppressIcon
              />
            );
          })}
        </RffGroup>
      )}
    </Flx>
  );
};

export default FileUploadSection;
